import { IStyleConfig } from '@cian/utils';
import * as PropTypes from 'prop-types';
import * as React from 'react';

import { RComponent, Unsubscribe } from './fixed_components_store';

export interface IFixedState {
  fixed: boolean;
}

export interface IFixedProps {
  styles?: IStyleConfig;
}

export interface INoFixedProps {
  hidden: boolean;
}

export interface IFixedComponentOwnProps {
  container: string;
}

interface IMakeFixedComponentOptions<Props> {
  id: string;
  Fixed: RComponent<Props & IFixedProps>;
  NoFixed: RComponent<Props & INoFixedProps>;
  shouldBeFixed(node: HTMLElement, props: Props & IFixedComponentOwnProps): boolean;
  getScrollableElement?(props: Props & IFixedComponentOwnProps): Element | Window;
}

export function makeFixedComponent<Props>({
  id,
  Fixed,
  NoFixed,
  shouldBeFixed,
  getScrollableElement,
}: IMakeFixedComponentOptions<Props>): React.ComponentClass<Props & IFixedComponentOwnProps> {
  Fixed.displayName = `fixed(${id})`;
  NoFixed.displayName = `noFixed(${id})`;

  type FixedComponentProps = Props & IFixedComponentOwnProps;
  class FixedComponent extends React.Component<FixedComponentProps, IFixedState> {
    public static displayName = `fixedComponent(${id})`;
    public static contextTypes = {
      fixedComponentsStore: PropTypes.object,
    };
    public state: IFixedState = {
      fixed: false,
    };
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public context: any;

    private node: HTMLElement | null;
    private scrollableElement: Element | Window;
    private unsubscribeFromForceReflow: Unsubscribe;

    public componentDidMount() {
      this.scrollableElement = getScrollableElement ? getScrollableElement(this.props as FixedComponentProps) : window;
      this.scrollableElement.addEventListener('scroll', this.handleScroll);
      this.scrollableElement.addEventListener('orientationchange', this.handleScroll);
      this.unsubscribeFromForceReflow = this.context.fixedComponentsStore.onForceReflow(this.handleScroll);
      this.handleScroll();
    }

    public componentWillUnmount() {
      this.scrollableElement.removeEventListener('scroll', this.handleScroll);
      this.scrollableElement.removeEventListener('orientationchange', this.handleScroll);
      this.context.fixedComponentsStore.removeElement(this.props.container, id);
      this.unsubscribeFromForceReflow();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: FixedComponentProps) {
      this.updateElement(nextProps);
    }

    public render() {
      return (
        <div ref={this.setNode}>
          <NoFixed hidden={this.state.fixed} {...this.props} />
        </div>
      );
    }

    private handleScroll = () => {
      if (!this.node) {
        return;
      }
      const isShouldBeFixed = shouldBeFixed(this.node, this.props as FixedComponentProps);
      if (this.state.fixed !== isShouldBeFixed) {
        this.setState({ fixed: isShouldBeFixed }, () => this.updateElement(this.props as FixedComponentProps));
      }
    };

    private setNode = (node: HTMLElement | null) => {
      this.node = node;
    };

    private updateElement = (props: FixedComponentProps) => {
      if (this.state.fixed) {
        this.context.fixedComponentsStore.setElement(this.props.container, id, Fixed, props);
      } else {
        this.context.fixedComponentsStore.removeElement(this.props.container, id);
      }
    };
  }

  return FixedComponent;
}

interface IFixedContainerProps {
  name: string;
}

type FixedContainerProps = IFixedContainerProps & IFixedProps;

export class FixedContainer extends React.Component<FixedContainerProps, object> {
  private unsubscribe: Unsubscribe;
  public static contextTypes = {
    fixedComponentsStore: PropTypes.object,
  };
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public context: any;

  public UNSAFE_componentWillMount() {
    this.unsubscribe = this.context.fixedComponentsStore.subscribe(() => {
      this.forceUpdate();
    });
  }

  public componentWillUnmount() {
    this.unsubscribe();
  }

  public render() {
    const { styles } = this.props;
    const elements = this.context.fixedComponentsStore.getElements(this.props.name);
    const elementsWithKeys = Object.keys(elements).map(key => {
      const { Component, props } = elements[key];

      return <Component key={key} {...{ ...props, styles }} />;
    });

    return <div>{elementsWithKeys}</div>;
  }
}
